/**
 * Multi-session chat history persistence for the XAES Analyst.
 *
 * Each `(userId, workshopId)` scope holds a *list* of `ChatSession`s —
 * history is private to the signed-in user and never shared across
 * accounts on the same browser profile. Sessions are sorted
 * newest-first on load.
 *
 * The live in-memory buffer (current chat) is owned by `use-chat.ts`.
 * After a fresh login / page reload the most recent saved session is
 * auto-resumed into the buffer so conversations survive auth cycles;
 * History still lists every persisted session for that user+scope.
 *
 * Storage layout (idb-keyval):
 *
 *   - `xaes-chat-sessions:<userId>:<workshopId>` → `ChatSession[]`
 *   - Legacy unscoped `xaes-chat-sessions:<workshopId>` is claimed
 *     into the current user's key on first read, then deleted.
 *   - Legacy `xaes2-chat-<workshopId>` (single flat `ChatMessage[]`) is
 *     migrated to a single auto-titled session on first read and the
 *     old key is then deleted.
 *
 * Source references:
 *   - Loveable v1 reference: `src/hooks/use-xaes-llm.tsx` — single
 *     in-memory chat with no persistence. Multi-session is new to the
 *     rebuild's chat overhaul.
 *   - ADR 0001 — chat history is local-only.
 *   - Predecessor of this file persisted a single `ChatMessage[]`;
 *     legacy data is migrated forward, not dropped.
 */

import {
  scopedIdbGet as get,
  scopedIdbSet as set,
  scopedIdbDel as del,
} from "@/lib/client-scoped-idb";
import type { ChatMessage } from "./chat-runtime-store";

/** Per-user + scope list namespace. */
const sessionsKeyFor = (userId: string, workshopId: string): string =>
  `xaes-chat-sessions:${encodeURIComponent(userId)}:${workshopId}`;

/** Pre-per-user namespace — claimed into the user's key on first read. */
const unscopedKeyFor = (workshopId: string): string =>
  `xaes-chat-sessions:${workshopId}`;

/** Legacy single-chat namespace — migrated on first read of the new key. */
const legacyKeyFor = (workshopId: string): string => `xaes2-chat-${workshopId}`;

/** Default title until the user sends their first message. */
const NEW_CHAT_TITLE = "New chat";

/** Cap chat session titles so they fit the dropdown row without truncation
 *  trickery in the renderer. */
const TITLE_MAX_LEN = 50;

/**
 * A single saved chat conversation. Sessions live keyed by their
 * `id` inside the per-user, per-workshop list.
 */
export type ChatSession = {
  /** Stable uuid-ish identifier — generated via `crypto.randomUUID()`. */
  id: string;
  /** Conversation scope this session belongs to (workshop id or `"MC"`). */
  workshopId: string;
  /** First user message truncated to ~50 chars, or `"New chat"` until send. */
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
};

/** Derive a session title from the first user message text. Falls back
 *  to `"New chat"` for empty/whitespace-only inputs. */
export function titleFromFirstMessage(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, " ");
  if (!trimmed) return NEW_CHAT_TITLE;
  if (trimmed.length <= TITLE_MAX_LEN) return trimmed;
  return `${trimmed.slice(0, TITLE_MAX_LEN - 1).trimEnd()}…`;
}

/** Generate a fresh session id. Falls back to a manual generator on
 *  the few embeds without `crypto.randomUUID` (older test runners). */
export function newSessionId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `cs-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Strip in-flight / errored / empty-text bubbles out of a session
 *  before persistence so the next load can't hand the AI SDK an empty
 *  text block (Anthropic rejects those — see `use-chat.ts toCoreMessages`).
 *  Mirrors the predecessor `saveChat` filter. */
function cleanMessagesForPersistence(messages: ChatMessage[]): ChatMessage[] {
  return messages
    .filter((m) => !m.streaming && !m.error && m.text.trim().length > 0)
    .map(({ id, role, text, references, sources, citedIndices }) => ({
      id,
      role,
      text,
      references,
      // Keep grounding sources so a reloaded session still shows citations,
      // along with the resolved cited-index set so the panel reloads in its
      // final "Sources" vs "Retrieved context" state rather than blank (#460).
      sources,
      citedIndices,
    }));
}

/** Coerce arbitrary IDB-read data into a safe `ChatSession`. Returns
 *  `null` if the shape can't be salvaged. */
function sanitiseSession(raw: unknown, workshopId: string): ChatSession | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || r.id.length === 0) return null;
  const messages = Array.isArray(r.messages)
    ? (r.messages.filter(
        (m): m is ChatMessage =>
          typeof m === "object" &&
          m !== null &&
          typeof (m as ChatMessage).id === "string" &&
          ((m as ChatMessage).role === "user" ||
            (m as ChatMessage).role === "assistant") &&
          typeof (m as ChatMessage).text === "string",
      ) as ChatMessage[])
    : [];
  const createdAt = typeof r.createdAt === "number" ? r.createdAt : Date.now();
  const updatedAt = typeof r.updatedAt === "number" ? r.updatedAt : createdAt;
  const title =
    typeof r.title === "string" && r.title.length > 0 ? r.title : NEW_CHAT_TITLE;
  return {
    id: r.id,
    workshopId:
      typeof r.workshopId === "string" && r.workshopId.length > 0
        ? r.workshopId
        : workshopId,
    title,
    createdAt,
    updatedAt,
    messages: cleanMessagesForPersistence(messages),
  };
}

function sanitiseList(raw: unknown, workshopId: string): ChatSession[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s) => sanitiseSession(s, workshopId))
    .filter((s): s is ChatSession => s !== null)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * Claim a pre-per-user `xaes-chat-sessions:<workshopId>` list into the
 * current user's key. First user to open the scope after the upgrade
 * owns the data; the unscoped key is then deleted so other users don't
 * share it.
 */
async function migrateUnscopedIfNeeded(
  userId: string,
  workshopId: string,
): Promise<ChatSession[] | null> {
  try {
    const unscoped = await get<unknown>(unscopedKeyFor(workshopId));
    if (!Array.isArray(unscoped) || unscoped.length === 0) {
      if (unscoped !== undefined) await del(unscopedKeyFor(workshopId));
      return null;
    }
    const cleaned = sanitiseList(unscoped, workshopId);
    if (cleaned.length === 0) {
      await del(unscopedKeyFor(workshopId));
      return null;
    }
    await set(sessionsKeyFor(userId, workshopId), cleaned);
    await del(unscopedKeyFor(workshopId));
    return cleaned;
  } catch {
    return null;
  }
}

/**
 * One-shot migration: if a legacy `xaes2-chat-<workshopId>` key exists,
 * wrap its messages in a single session under the user-scoped key,
 * then delete the legacy entry. No-op when the legacy key is absent.
 */
async function migrateLegacyIfNeeded(
  userId: string,
  workshopId: string,
): Promise<ChatSession[] | null> {
  try {
    const legacyRaw = await get<unknown>(legacyKeyFor(workshopId));
    if (!Array.isArray(legacyRaw) || legacyRaw.length === 0) {
      // Nothing to migrate. Best-effort drop the empty/legacy key so
      // we don't repeat the read on every load.
      if (legacyRaw !== undefined) await del(legacyKeyFor(workshopId));
      return null;
    }
    const legacyMessages = legacyRaw.filter(
      (m): m is ChatMessage =>
        typeof m === "object" &&
        m !== null &&
        typeof (m as ChatMessage).id === "string" &&
        typeof (m as ChatMessage).text === "string" &&
        ((m as ChatMessage).role === "user" ||
          (m as ChatMessage).role === "assistant"),
    );
    const firstUser = legacyMessages.find((m) => m.role === "user");
    const now = Date.now();
    const session: ChatSession = {
      id: newSessionId(),
      workshopId,
      title: firstUser ? titleFromFirstMessage(firstUser.text) : NEW_CHAT_TITLE,
      createdAt: now,
      updatedAt: now,
      messages: cleanMessagesForPersistence(legacyMessages),
    };
    await set(sessionsKeyFor(userId, workshopId), [session]);
    await del(legacyKeyFor(workshopId));
    return [session];
  } catch {
    return null;
  }
}

/**
 * Load all persisted sessions for the given user + scope, sorted by
 * `updatedAt` desc. Migrates unscoped / legacy data forward on first
 * read. When `claimFromUserId` is set and the primary key is empty,
 * that user's list is claimed into the primary key (used when switching
 * ZITADEL persistence from preferred_username → stable `sub`).
 * Returns `[]` on read failure — chat persistence is best-effort
 * and never blocks the panel.
 */
export async function loadSessions(
  userId: string,
  workshopId: string,
  claimFromUserId?: string,
): Promise<ChatSession[]> {
  try {
    const key = sessionsKeyFor(userId, workshopId);
    const raw = await get<unknown>(key);
    if (Array.isArray(raw)) {
      return sanitiseList(raw, workshopId);
    }
    // Claim prior username-keyed history into the stable subject key.
    if (
      claimFromUserId &&
      claimFromUserId.length > 0 &&
      claimFromUserId !== userId
    ) {
      const priorKey = sessionsKeyFor(claimFromUserId, workshopId);
      const priorRaw = await get<unknown>(priorKey);
      if (Array.isArray(priorRaw) && priorRaw.length > 0) {
        const claimed = sanitiseList(priorRaw, workshopId);
        await set(key, claimed);
        await del(priorKey);
        return claimed;
      }
    }
    // No user-scoped data — try claiming the unscoped key, then the
    // very-old single-chat legacy format.
    const fromUnscoped = await migrateUnscopedIfNeeded(userId, workshopId);
    if (fromUnscoped) return fromUnscoped;
    const migrated = await migrateLegacyIfNeeded(userId, workshopId);
    return migrated ?? [];
  } catch {
    return [];
  }
}

/**
 * Upsert a single session into its user+scope list. Cleans message-level
 * stream / error metadata before writing, mirroring the predecessor
 * `saveChat`. Best-effort — silently no-ops on write failure.
 */
export async function saveSession(
  userId: string,
  session: ChatSession,
): Promise<void> {
  try {
    const key = sessionsKeyFor(userId, session.workshopId);
    const existing = sanitiseList(await get<unknown>(key), session.workshopId);
    const next: ChatSession = {
      ...session,
      messages: cleanMessagesForPersistence(session.messages),
    };
    const idx = existing.findIndex((s) => s.id === next.id);
    if (idx >= 0) {
      existing[idx] = next;
    } else {
      existing.push(next);
    }
    existing.sort((a, b) => b.updatedAt - a.updatedAt);
    await set(key, existing);
  } catch {
    /* see jsdoc */
  }
}

/** Drop a single session from the user+scope list. No-op if not found. */
export async function deleteSession(
  userId: string,
  workshopId: string,
  sessionId: string,
): Promise<void> {
  try {
    const key = sessionsKeyFor(userId, workshopId);
    const existing = sanitiseList(await get<unknown>(key), workshopId);
    const next = existing.filter((s) => s.id !== sessionId);
    if (next.length === existing.length) return;
    await set(key, next);
  } catch {
    /* ignore */
  }
}

/** Erase the entire session list for a user+scope. Used by the panel's
 *  `reset` affordance — kept on parity with the predecessor API so
 *  callers like the settings modal don't break. */
export async function clearAllSessions(
  userId: string,
  workshopId: string,
): Promise<void> {
  try {
    await del(sessionsKeyFor(userId, workshopId));
  } catch {
    /* ignore */
  }
}

/** Test helper — mirrors the key layout used by the store. */
export function sessionsStorageKey(userId: string, workshopId: string): string {
  return sessionsKeyFor(userId, workshopId);
}
